import type {IconType} from '@Components/icon-v2/icon.types';
import type {MediaItemData} from '@Components/simple-media-carousel/simple-media-carousel.types';
import type {GraphicsMetaMapType, PageHashedId, PosterHashedId} from '@Components/social-media/publish-params.types';
import type {DesignSelection} from '@Components/social-media/social-media.types';

export enum PreviewMediaLayout {
  SINGLE = 'SINGLE',
  SIDE_BY_SIDE = 'SIDE_BY_SIDE',
  CAROUSEL = 'CAROUSEL',
  STRIP = 'STRIP',
}

export enum PreviewMediaFit {
  CONTAIN = 'CONTAIN',
  COVER = 'COVER',
}

export enum PreviewMediaBackdrop {
  DARK = 'DARK',
  LIGHT = 'LIGHT',
}

export interface PreviewMediaOptions {
  layout: PreviewMediaLayout;
  fit?: PreviewMediaFit;
  backdrop?: PreviewMediaBackdrop;
  frameClassName?: string;
  carouselClassName?: string;
  carouselButtonContainerClassName?: string;
  areCarouselArrowsAlwaysVisible?: boolean;
  carouselButtonType?: IconType;
  carouselIndicatorClassName?: string;
  carouselCounterClassName?: string;
  enableVideoAutoplay?: boolean;
}

export interface SocialPostContentMediaWrapperProps extends Partial<PreviewMediaOptions> {
  designs: DesignSelection[];
  designsGraphicsMetaMap: GraphicsMetaMapType;
  graphicsLoadingMap: Record<PosterHashedId, Record<PageHashedId, boolean>>;
  doRetryOnError?: boolean;
  onAssetErrored?: (design: DesignSelection) => void;
  onAssetLoad?: (design: DesignSelection) => void;
  onOrphanedPreviewContainerClick?: VoidFunction;
}

export const MIN_PAGES_FOR_MULTI_PAGE_LAYOUT = 2;

export const getFeedMediaLayout = (numberOfPages: number): PreviewMediaLayout => {
  return numberOfPages >= MIN_PAGES_FOR_MULTI_PAGE_LAYOUT ? PreviewMediaLayout.SIDE_BY_SIDE : PreviewMediaLayout.SINGLE;
};

export const getStripMediaLayout = (numberOfPages: number): PreviewMediaLayout => {
  return numberOfPages >= MIN_PAGES_FOR_MULTI_PAGE_LAYOUT ? PreviewMediaLayout.STRIP : PreviewMediaLayout.SINGLE;
};

export interface OrderedMediaItem extends MediaItemData {
  order: number;
}

export interface AssetRetryCacheBusting {
  isCacheBustingNeeded: boolean;
  scheduleRetry: VoidFunction;
  stopCacheBusting: VoidFunction;
}

export const MAX_SIDE_BY_SIDE_CELLS = 4;
export const SIDE_BY_SIDE_CELLS_PER_ROW = 2;
export const MIN_SIDE_BY_SIDE_CELLS = 1;
export const PREVIEW_ASSET_ELEMENT_ID_PREFIX = 'preview-grid-image-';
export const FIRST_ASSET_ELEMENT_NUMBER = 1;
export const MAX_ASSET_RETRIES = 40;
export const ASSET_RETRY_TIMEOUT_MS = 5000;
export const FIRST_FRAME_SEEK_SECONDS = 0.1;
